import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ContentCard,
  DashboardHeader,
  Icons,
} from "../common/DashboardComponents";
import StatusBadge from "../common/StatusBadge";
import Loader from "../common/Loader";
import "bootstrap-icons/font/bootstrap-icons.css";
import { useDispatch, useSelector } from "react-redux";
import { fetchIDCard } from "../../redux/actions/grievanceActions1";

const IdCardList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Get idCard data from Redux store
  const { idCard } = useSelector((state) => state.grievances);

  const [idCards, setIdCards] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [cardsPerPage, setCardsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch data on component mount
  useEffect(() => {
    setIsLoading(true);
    dispatch(fetchIDCard());
    setIsLoading(false);
  }, [dispatch]);

  // Update local state when Redux data changes
  useEffect(() => {
    setIdCards(idCard);
  }, [idCard]);

  // Calculate pagination variables
  const indexOfLastCard = currentPage * cardsPerPage;
  const indexOfFirstCard = indexOfLastCard - cardsPerPage;
  const currentCards = idCards.slice(indexOfFirstCard, indexOfLastCard);
  const totalPages = Math.ceil(idCards.length / cardsPerPage);

  // Pagination functions
  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  // Navigate to details page
  const handleViewDetails = (idCard) => {
    navigate("/IdCardDetails", {
      state: {
        id: idCard.CARD_ID,
        esm: idCard.ESM_ID,
        userName: idCard.USER_NAME,
        centerName: idCard.UNIT_CENTRE_NAME,
      },
    });
  };

  // Generate page numbers for display
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      // If total pages less than max, show all pages
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Always show first page
      pageNumbers.push(1);

      // Calculate start and end of middle range
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);

      // Adjust if at the beginning or end
      if (currentPage <= 2) {
        endPage = 3;
      } else if (currentPage >= totalPages - 1) {
        startPage = totalPages - 2;
      }

      // Add ellipsis after first page if needed
      if (startPage > 2) {
        pageNumbers.push("...");
      }

      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      // Add ellipsis before last page if needed
      if (endPage < totalPages - 1) {
        pageNumbers.push("...");
      }

      // Always show last page
      pageNumbers.push(totalPages);
    }

    return pageNumbers;
  };

  return (
    <div className="container-fluid">
      <DashboardHeader title="ID Card Management"  />

      <ContentCard title="ID Card List"   icon={Icons.IDCard}>
        {isLoading ? (
          <Loader message="Loading ID cards..." />
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <select
                  className="form-select form-select-sm"
                  value={cardsPerPage}
                  onChange={(e) => {
                    setCardsPerPage(Number(e.target.value));
                    setCurrentPage(1); // Reset to first page when changing items per page
                  }}
                  style={{ width: "auto" }}
                >
                  <option value={5}>5 per page</option>
                  <option value={10}>10 per page</option>
                  <option value={15}>15 per page</option>
                  <option value={20}>20 per page</option>
                </select>
              </div>
              <div className="text-muted small">
                Showing {idCards.length > 0 ? indexOfFirstCard + 1 : 0} to{" "}
                {Math.min(indexOfLastCard, idCards.length)} of {idCards.length}{" "}
                ID cards
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="bg-light">
                  <tr>
                    <th className="py-3">Card ID</th>
                    <th className="py-3">Applicant Name</th>
                    <th className="py-3">Card Type</th>
                    <th className="py-3">Center Name</th>
                    <th className="py-3">Status</th>
                    <th className="py-3">Date Applied</th>
                    <th className="py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentCards.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center py-4">
                        <i className="bi bi-exclamation-circle text-muted fs-4 d-block mb-2"></i>
                        <p className="text-muted mb-0">No ID cards found</p>
                      </td>
                    </tr>
                  ) : (
                    currentCards.map((idCard) => (
                      <tr key={idCard.CARD_ID} className="align-middle">
                        <td>{idCard.CARD_ID}</td>
                        <td>{idCard.USER_NAME}</td>
                        <td>{idCard.IDCARD_TYPE_NAME}</td>
                        <td>{idCard.UNIT_CENTRE_NAME}</td>
                        <td>
                          <StatusBadge
                            status={idCard.STATUS_NAME}
                            isGrant={true}
                          />
                        </td>
                        <td>{idCard.ENTRY_DATETIME}</td>
                        <td>
                          <button
                            onClick={() => handleViewDetails(idCard)}
                            className="btn btn-sm btn-primary rounded-pill px-3"
                          >
                            <i className="bi bi-eye-fill me-1"></i> View
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {idCards.length > 0 && (
              <div className="d-flex justify-content-between align-items-center mt-4">
                <div className="text-muted small">
                  Page {currentPage} of {totalPages}
                </div>

                <nav aria-label="ID card pagination">
                  <ul className="pagination mb-0">
                    {/* First page button */}
                    <li
                      className={`page-item ${
                        currentPage === 1 ? "disabled" : ""
                      }`}
                    >
                      <button
                        onClick={() => paginate(1)}
                        className="page-link"
                        aria-label="First"
                      >
                        <i className="bi bi-chevron-double-left"></i>
                      </button>
                    </li>

                    {/* Previous button */}
                    <li
                      className={`page-item ${
                        currentPage === 1 ? "disabled" : ""
                      }`}
                    >
                      <button
                        onClick={() => paginate(currentPage - 1)}
                        className="page-link"
                        aria-label="Previous"
                      >
                        <i className="bi bi-chevron-left"></i>
                      </button>
                    </li>

                    {/* Page numbers */}
                    {getPageNumbers().map((pageNum, index) => (
                      <li
                        key={index}
                        className={`page-item ${
                          pageNum === currentPage ? "active" : ""
                        } ${pageNum === "..." ? "disabled" : ""}`}
                      >
                        <button
                          onClick={() =>
                            pageNum !== "..." ? paginate(pageNum) : null
                          }
                          className={`page-link ${
                            pageNum === currentPage
                              ? "bg-primary text-white"
                              : ""
                          }`}
                        >
                          {pageNum}
                        </button>
                      </li>
                    ))}

                    {/* Next button */}
                    <li
                      className={`page-item ${
                        currentPage === totalPages ? "disabled" : ""
                      }`}
                    >
                      <button
                        onClick={() => paginate(currentPage + 1)}
                        className="page-link"
                        aria-label="Next"
                      >
                        <i className="bi bi-chevron-right"></i>
                      </button>
                    </li>

                    {/* Last page button */}
                    <li
                      className={`page-item ${
                        currentPage === totalPages ? "disabled" : ""
                      }`}
                    >
                      <button
                        onClick={() => paginate(totalPages)}
                        className="page-link"
                        aria-label="Last"
                      >
                        <i className="bi bi-chevron-double-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </>
        )}
      </ContentCard>
    </div>
  );
};

export default IdCardList;
